import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NumberPad } from '../NumberPad';
import { MoneyValue } from '../MoneyValue';
import { cn } from '@/lib/utils';

interface CashPanelProps {
  total: number;
  isSubmitting: boolean;
  onConfirm: (amountTendered: number) => void;
}

/** Cash tendered + change calculation. Change = tendered − total (display only;
 *  the BE returns the authoritative changeDue). */
export function CashPanel({ total, isSubmitting, onConfirm }: CashPanelProps) {
  const [tendered, setTendered] = useState(0);
  const change = tendered - total;
  const enough = tendered >= total && total > 0;

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <div className="flex flex-col justify-between gap-4">
        <div className="space-y-3">
          <div className="rounded-lg bg-secondary px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Total tagihan</p>
            <MoneyValue value={total} emphasis className="text-3xl" />
          </div>
          <div className="rounded-lg border-2 border-border bg-card px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Uang diterima</p>
            <MoneyValue value={tendered} className="text-2xl" />
          </div>
          <div
            className={cn(
              'rounded-lg px-4 py-3 transition-colors',
              enough ? 'bg-gold-200' : 'bg-destructive/10',
            )}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">
              {change >= 0 ? 'Kembalian' : 'Kurang'}
            </p>
            <MoneyValue
              value={Math.abs(change)}
              emphasis={enough}
              className={cn('text-3xl', !enough && 'text-destructive')}
            />
          </div>
        </div>
        <Button
          size="lg"
          variant="gold"
          className="h-16 w-full text-xl"
          disabled={!enough || isSubmitting}
          onClick={() => onConfirm(tendered)}
        >
          {isSubmitting ? <Loader2 className="h-6 w-6 animate-spin" /> : 'Konfirmasi & Cetak'}
          {!isSubmitting && (
            <kbd className="ml-1 rounded border border-ink-900/20 px-1.5 text-xs">F9</kbd>
          )}
        </Button>
      </div>
      <NumberPad value={tendered} onChange={setTendered} cashChips />
    </div>
  );
}
